import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DashboardOverviewResponse, User, Tenant } from '../types/dashboard';
import { dashboardService, normalizeDashboardOverviewResponse } from '../services/dashboardService';

interface DashboardContextType {
  tenantId: string;
  userId: number;
  user: User | null;
  tenant: Tenant | null;
  dashboardData: DashboardOverviewResponse | null;
  isLoading: boolean;
  error: string | null;
  updateDashboardData: (data: Partial<DashboardOverviewResponse>) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  refreshDashboard: () => void;
}

interface DashboardProviderProps {
  children: ReactNode;
  tenantId: string;
  userId: number;
  user?: User | null;
  tenant?: Tenant | null;
}

const DashboardContext = createContext<DashboardContextType | null>(null);

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ 
  children, 
  tenantId, 
  userId,
  user = null,
  tenant = null
}) => {
  const [dashboardData, setDashboardData] = useState<DashboardOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateDashboardData = useCallback((data: Partial<DashboardOverviewResponse>) => {
    setDashboardData(prev => prev ? { ...prev, ...data } : null);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const refreshDashboard = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await dashboardService.getDashboardOverview(tenantId, userId);
      const normalized = normalizeDashboardOverviewResponse(response.data, null);
      if (normalized) {
        setDashboardData(normalized);
      } else {
        setError(response.error || 'Failed to load dashboard data');
      }
    } catch (error: any) {
      console.error('Error refreshing dashboard:', error);
      const errorMessage = error?.message || error?.error || 'Failed to load dashboard data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, userId]);

  const contextValue: DashboardContextType = {
    tenantId,
    userId,
    user,
    tenant,
    dashboardData,
    isLoading,
    error,
    updateDashboardData,
    setError,
    setLoading,
    refreshDashboard
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
};
